// Turns the plain text a chat message holds into text + link parts: web
// addresses become links, and, when the caller knows how to build one,
// phone numbers become WhatsApp links. No markdown, no HTML: the model's reply
// is rendered as text either way, only these two token kinds get an <a>.

export type LinkPart =
  | { type: "text"; value: string }
  | { type: "link"; href: string; label: string; kind: "url" | "phone" };

export interface LinkifyOptions {
  /** Builds the href for a phone-looking token (e.g. a wa.me link); return null to leave it as text. */
  phoneHref?: (raw: string) => string | null;
}

const URL_RE = /(?:https?:\/\/|www\.)[^\s<>"'`]+/gi;
// A phone number: 7–15 digits, optionally separated by spaces, dots, dashes
// or parentheses, optionally with a leading "+".
const PHONE_RE = /\+?\(?\d[\d\s().-]{5,}\d/g;
const TRAILING_PUNCT = /[.,;:!?]+$/;

const digitsOf = (s: string) => s.replace(/\D/g, "");

/** "2024-2025" is a season, not a number to call. */
const looksLikeYearRange = (raw: string) => {
  const m = raw.match(/^(\d{4})\s*[-–]\s*(\d{4})$/);
  if (!m) return false;
  const [a, b] = [Number(m[1]), Number(m[2])];
  return a >= 1900 && a <= 2100 && b >= 1900 && b <= 2100;
};

const trimUrl = (raw: string): string => {
  let url = raw.replace(TRAILING_PUNCT, "");
  // A closing bracket that has no opening one inside the URL belongs to the sentence.
  while (/[)\]}]$/.test(url)) {
    const close = url[url.length - 1];
    const open = close === ")" ? "(" : close === "]" ? "[" : "{";
    if (url.includes(open)) break;
    url = url.slice(0, -1).replace(TRAILING_PUNCT, "");
  }
  return url;
};

const pushText = (parts: LinkPart[], value: string) => {
  if (!value) return;
  const last = parts[parts.length - 1];
  if (last && last.type === "text") last.value += value;
  else parts.push({ type: "text", value });
};

const linkifyPhones = (parts: LinkPart[], text: string, phoneHref: LinkifyOptions["phoneHref"]) => {
  if (!phoneHref) {
    pushText(parts, text);
    return;
  }
  let cursor = 0;
  for (const m of text.matchAll(PHONE_RE)) {
    const raw = m[0];
    const digits = digitsOf(raw);
    const href = digits.length >= 7 && digits.length <= 15 && !looksLikeYearRange(raw) ? phoneHref(raw) : null;
    if (!href) continue;
    pushText(parts, text.slice(cursor, m.index));
    parts.push({ type: "link", href, label: raw, kind: "phone" });
    cursor = (m.index ?? 0) + raw.length;
  }
  pushText(parts, text.slice(cursor));
};

export const linkify = (text: string, opts: LinkifyOptions = {}): LinkPart[] => {
  const parts: LinkPart[] = [];
  let cursor = 0;
  for (const m of text.matchAll(URL_RE)) {
    const start = m.index ?? 0;
    const url = trimUrl(m[0]);
    if (!url) continue;
    const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    linkifyPhones(parts, text.slice(cursor, start), opts.phoneHref);
    parts.push({ type: "link", href, label: url, kind: "url" });
    cursor = start + url.length;
  }
  linkifyPhones(parts, text.slice(cursor), opts.phoneHref);
  return parts;
};
